import type { ToastProps } from '#ui/types';
import { Query, type QueryCacheNotifyEvent, useQueryClient } from '@tanstack/vue-query';
import { useWebSocket } from '@vueuse/core';
import lodashKeyBy from 'lodash-es/keyBy';

import {
    type AuctionDetailSnapshotWsEventResponseDto,
    AuctionDetailSnapshotWsEventResponseDtoEvent,
    type AuctionItemEntity,
    type AuctionListingSnapshotWsEventResponseDto,
    AuctionListingSnapshotWsEventResponseDtoEvent,
    type AuctionsListingResponseDto,
    type AuctionWonWsEventResponseDto,
    type AuthorizeSuccessWsEventResponseDto,
    type AuthorizeWsEventBodyDto,
    type DirectSaleDetailSnapshotWsEventResponseDto,
    DirectSaleDetailSnapshotWsEventResponseDtoEvent,
    type DirectSaleItemEntity,
    type DirectSaleNegotiationAbortedDueToAnotherAcceptanceWsEventResponseDto,
    type DirectSaleNegotiationAcceptedByBackofficeWsEventResponseDto,
    type DirectSaleNegotiationAcceptedByBuyerWsEventResponseDto,
    type DirectSaleNegotiationAcceptedBySellerWsEventResponseDto,
    type DirectSaleNegotiationExpiredWsEventResponseDto,
    type DirectSaleNegotiationProposalByBuyerWsEventResponseDto,
    type DirectSaleNegotiationProposalBySellerWsEventResponseDto,
    type DirectSalePurchasedWsEventResponseDto,
    type DirectSalesListingResponseDto,
    type DirectSalesListingSnapshotWsEventResponseDto,
    DirectSalesListingSnapshotWsEventResponseDtoEvent,
    type DirectSaleUserMetaSnapshotWsEventResponseDto,
    DirectSaleUserMetaSnapshotWsEventResponseDtoEvent,
    type NegotiationAcceptedByBackofficeWsEventResponseDto,
    type NegotiationAcceptedByBuyerWsEventResponseDto,
    type NegotiationAcceptedBySellerWsEventResponseDto,
    type NegotiationDeclinedBySellerWsEventResponseDto,
    type NegotiationExpiredWsEventResponseDto,
    type NegotiationProposalByBuyerWsEventResponseDto,
    type NegotiationProposalBySellerWsEventResponseDto,
    type NegotiationStartedWsEventResponseDto,
    type NegotiationStoppedProposalByBuyerWsEventResponseDto,
    type ObserveAuctionDetailWsBodyDto,
    type ObserveAuctionListingWsBodyDto,
    type ObserveDirectSaleDetailWsBodyDto,
    type ObserveDirectSalesListingWsBodyDto,
    type UnauthorizeSuccessWsEventResponseDto,
    type UserAutoBidMaxThresholdReachedWsEventResponseDto,
    type UserBailAuthorizedWsEventResponseDto,
    type UserMaxBidderRestoredWsEventResponseDto,
    type UserMetaSnapshotWsEventResponseDto,
    UserMetaSnapshotWsEventResponseDtoEvent,
    type UserOffersUpdateWsEventResponseDto,
    type UserOutBidWsEventResponseDto,
    UserOutBidWsEventResponseDtoDataReason,
    type UserWinningBidWsEventResponseDto,
    type WsEventDtoEvents,
    WsInEventEnum,
    WsOutEvent,
    type UserFavoriteAssetsSnapshotWsEventResponseDto,
    type UserFavoriteAssetsAddedWsEventResponseDto,
    type UserFavoriteAssetsRemovedWsEventResponseDto,
    getApiAccountAuctionsBuyerQueryKey,
    getApiAccountAuctionsSellerQueryKey,
    getApiAccountDirectSalesBuyerQueryKey,
    getApiAccountDirectSalesSellerQueryKey,
    getApiAuctionDetailQueryKey,
    getApiAuctionsListingQueryKey,
    getApiDirectSaleDetailQueryKey,
    getApiDirectSalesListingQueryKey,
} from '~/apiClient';
import { ROUTE_NAMES } from '~/constants/routes';
import { NotificationDuration } from '~/types/notifications.type';

type WsMessage = WsEventDtoEvents | UserMetaSnapshotWsEventResponseDto;

enum WsEntityType {
    AuctionsListing,
    AuctionDetail,
    DirectSalesListing,
    DirectSaleDetail,
}

const QUERY_KEY_MATCH_ALL_PLACEHOLDER = '#ANY#';

const makeKeyMatcher =
    (...patterns: readonly (readonly unknown[])[]): ((query: Query) => boolean) =>
    (query: Query): boolean =>
        patterns.some(
            pattern =>
                query.queryKey.length >= pattern.length &&
                pattern.every((segment, i) => segment === QUERY_KEY_MATCH_ALL_PLACEHOLDER || segment === query.queryKey[i])
        );

const WS_ENTITY_TYPE_TO_PREDICATE_MAP: Record<WsEntityType, (query: Query) => boolean> = {
    [WsEntityType.AuctionsListing]: makeKeyMatcher(getApiAuctionsListingQueryKey(), getApiAccountAuctionsSellerQueryKey()),
    [WsEntityType.AuctionDetail]: makeKeyMatcher(getApiAuctionDetailQueryKey(QUERY_KEY_MATCH_ALL_PLACEHOLDER)),
    [WsEntityType.DirectSalesListing]: makeKeyMatcher(
        getApiDirectSalesListingQueryKey(),
        getApiAccountDirectSalesSellerQueryKey(),
        getApiAccountDirectSalesBuyerQueryKey()
    ),
    [WsEntityType.DirectSaleDetail]: makeKeyMatcher(getApiDirectSaleDetailQueryKey(QUERY_KEY_MATCH_ALL_PLACEHOLDER)),
};

type WsEntityManagerReturn = {
    sendObserveMessage: (options?: { subscribeOnly?: boolean }) => void;
    handleSnapshotMessage: (message: WsMessage) => boolean;
};

type MakeWsEntityManager = (entityType: WsEntityType) => WsEntityManagerReturn;

type AuthHandshakeManager = {
    init: VoidFunction;
    resolve: VoidFunction;
    wait: () => Promise<void>;
};

type FavoriteAssetsInitFallbackManager = {
    start: VoidFunction;
    abort: VoidFunction;
};

const authHandshakeManager = ((): AuthHandshakeManager => {
    let handshakeDefer: PromiseWithResolvers<void> | null = null;

    return {
        init: (): void => {
            handshakeDefer = Promise.withResolvers();
        },
        resolve: (): void => {
            handshakeDefer?.resolve();
        },
        wait: (): Promise<void> => handshakeDefer?.promise ?? Promise.resolve(),
    };
})();

const isAuctionListingSnapshot = (message: WsMessage): message is AuctionListingSnapshotWsEventResponseDto =>
    message.event === AuctionListingSnapshotWsEventResponseDtoEvent.AuctionListingSnapshot;

const isAuctionDetailSnapshot = (message: WsMessage): message is AuctionDetailSnapshotWsEventResponseDto =>
    message.event === AuctionDetailSnapshotWsEventResponseDtoEvent.AuctionDetailSnapshot;

const isDirectSalesListingSnapshot = (message: WsMessage): message is DirectSalesListingSnapshotWsEventResponseDto =>
    message.event === DirectSalesListingSnapshotWsEventResponseDtoEvent.DirectSalesListingSnapshot;

const isDirectSaleDetailSnapshot = (message: WsMessage): message is DirectSaleDetailSnapshotWsEventResponseDto =>
    message.event === DirectSaleDetailSnapshotWsEventResponseDtoEvent.DirectSaleDetailSnapshot;

const isAuctionUserMetaSnapshot = (message: WsMessage): message is UserMetaSnapshotWsEventResponseDto =>
    message.event === UserMetaSnapshotWsEventResponseDtoEvent.UserMetaSnapshot;

const isDirectSaleUserMetaSnapshot = (message: WsMessage): message is DirectSaleUserMetaSnapshotWsEventResponseDto =>
    message.event === DirectSaleUserMetaSnapshotWsEventResponseDtoEvent.DirectSaleUserMetaSnapshot;

const makeBaseMessageHandler =
    <T>(event: WsOutEvent, handler: (message: T) => void) =>
    (message: WsMessage): boolean => {
        if ((message.event as string) === (event as string)) {
            handler(message as T);

            return true;
        } else {
            return false;
        }
    };

export default defineNuxtPlugin({
    name: 'webSocket',
    dependsOn: ['vueQuery', 'favoritesCache'],

    setup(nuxtApp) {
        const runtimeConfig = useRuntimeConfig();
        const { webSocket: webSocketConfig } = useAppConfig();
        const {
            isLoggedIn,
            state: { rawToken, data: authData },
        } = useAuthUtils();
        const queryClient = useQueryClient();
        const queryCache = queryClient.getQueryCache();
        const safeJsonParse = useSafeJsonParse();
        const localePath = useLocalePath();
        const { notifyWarning, notifySuccess, notifyInfo } = useNotification();
        const route = useRoute();
        const routeBaseName = useRouteBaseName();
        const { tt, tn } = useTypedI18n();
        const { logError, logWarn } = useLogs();
        const { updateTrackingCompanyMetrics } = useTracking();
        const { formatPurchaseFlowRoute } = useOrderPurchaseFlowGuard();
        const { setFavoriteAssets, addFavoriteAsset, deleteFavoriteAsset, isFavoriteAssetsInitialized } = useNuxtApp().$favoritesCache;

        const wsEndpoint = `${runtimeConfig.public.wsBaseUrl}/ws`;

        const messageFormatter = {
            ping: (): string => JSON.stringify({ event: WsInEventEnum.Ping }),

            authorize: (token: string): string =>
                JSON.stringify({ event: WsInEventEnum.Authorize, data: { token } satisfies AuthorizeWsEventBodyDto }),

            unauthorize: (): string => JSON.stringify({ event: WsInEventEnum.Unauthorize }),

            observeAuctionsListing: (ids: string[]): string =>
                JSON.stringify({
                    event: WsInEventEnum.ObserveAuctionListing,
                    data: { auctions: ids } satisfies ObserveAuctionListingWsBodyDto,
                }),

            observeAuctionDetail: (id: string | null): string =>
                JSON.stringify({
                    event: WsInEventEnum.ObserveAuctionDetail,
                    data: { auctionId: id } satisfies ObserveAuctionDetailWsBodyDto,
                }),

            observeDirectSalesListing: (ids: string[]): string =>
                JSON.stringify({
                    event: WsInEventEnum.ObserveDirectSalesListing,
                    data: { directSales: ids } satisfies ObserveDirectSalesListingWsBodyDto,
                }),

            observeDirectSalesDetail: (id: string | null): string =>
                JSON.stringify({
                    event: WsInEventEnum.ObserveDirectSaleDetail,
                    data: { directSaleId: id } satisfies ObserveDirectSaleDetailWsBodyDto,
                }),
        };

        const isSalePage = (message: { data: { slug: string } }): boolean =>
            isSdpRoute(routeBaseName(route)) && route.params.slug === message.data.slug;

        const formatNotifySubject = (
            message: { data: { slug: string; brand: string; model: string; version: string } },
            inPageTranslation: TranslationKey,
            linkedPageTranslation: TranslationKey
        ): string =>
            isSalePage(message)
                ? tt(inPageTranslation)
                : tt(linkedPageTranslation, {
                      brand: message.data.brand,
                      model: message.data.model,
                      version: message.data.version,
                  });

        const formatAuctionLinkNotifyActions = (message: { data: { slug: string } }, subpage?: 'negotiation'): ToastProps['actions'] | undefined =>
            isSalePage(message)
                ? undefined
                : [
                      {
                          label: tt('common.goToAuction'),
                          onClick: (): void => {
                              navigateTo(
                                  localePath({
                                      name: subpage === 'negotiation' ? ROUTE_NAMES.AUCTION_DETAIL_NEGOTIATION : ROUTE_NAMES.AUCTION_DETAIL,
                                      params: { slug: message.data.slug },
                                  })
                              );
                          },
                      },
                  ];

        const formatDirectSaleLinkNotifyActions = (
            message: { data: { slug: string } },
            subpage?: 'negotiation'
        ): ToastProps['actions'] | undefined =>
            isSalePage(message)
                ? undefined
                : [
                      {
                          label: tt('common.goToDirectSale'),
                          onClick: (): void => {
                              navigateTo(
                                  localePath({
                                      name: subpage === 'negotiation' ? ROUTE_NAMES.DIRECT_SALE_DETAIL_NEGOTIATION : ROUTE_NAMES.DIRECT_SALE_DETAIL,
                                      params: { slug: message.data.slug },
                                  })
                              );
                          },
                      },
                  ];

        const makeWsEntityManager: MakeWsEntityManager = entityType => {
            const predicate = WS_ENTITY_TYPE_TO_PREDICATE_MAP[entityType];
            const observedIds = ref<string[]>([]);

            const getFormattedMessage = (): string => {
                switch (entityType) {
                    case WsEntityType.AuctionsListing:
                        return messageFormatter.observeAuctionsListing(observedIds.value);
                    case WsEntityType.AuctionDetail:
                        return messageFormatter.observeAuctionDetail(observedIds.value[0] ?? null);
                    case WsEntityType.DirectSalesListing:
                        return messageFormatter.observeDirectSalesListing(observedIds.value);
                    case WsEntityType.DirectSaleDetail:
                        return messageFormatter.observeDirectSalesDetail(observedIds.value[0] ?? null);
                }
            };

            const sendObserveMessage: WsEntityManagerReturn['sendObserveMessage'] = ({ subscribeOnly } = {}) => {
                (!subscribeOnly || observedIds.value.length) &&
                    authHandshakeManager.wait().then(() => {
                        send(getFormattedMessage());
                    });
            };

            const handleSnapshotMessage = (message: WsMessage): boolean => {
                if (entityType === WsEntityType.AuctionsListing && isAuctionListingSnapshot(message)) {
                    const { data: snapshotData } = message;
                    const snapshotDataMap = lodashKeyBy(snapshotData, 'id');

                    const cache = getCache<AuctionsListingResponseDto>({ active: true });
                    const cacheData = cache?.state.data;

                    if (cacheData) {
                        cache.setData({
                            ...cacheData,
                            auctions: cacheData.auctions.map(auction => ({ ...auction, ...snapshotDataMap[auction.id] })),
                        });
                    }

                    return true;
                }

                if (entityType === WsEntityType.AuctionDetail && (isAuctionDetailSnapshot(message) || isAuctionUserMetaSnapshot(message))) {
                    const { data: snapshotData } = message;

                    const cache = getCache<AuctionItemEntity>({ active: true });
                    const cacheData = cache?.state.data;

                    if (cacheData) {
                        cache.setData({ ...cacheData, ...snapshotData });
                    }

                    return true;
                }

                if (entityType === WsEntityType.DirectSalesListing && isDirectSalesListingSnapshot(message)) {
                    const { data: snapshotData } = message;
                    const snapshotDataMap = lodashKeyBy(snapshotData, 'id');

                    const cache = getCache<DirectSalesListingResponseDto>({ active: true });
                    const cacheData = cache?.state.data;

                    if (cacheData) {
                        cache.setData({
                            ...cacheData,
                            directSales: cacheData.directSales.map(directSale => ({ ...directSale, ...snapshotDataMap[directSale.id] })),
                        });
                    }

                    return true;
                }

                if (entityType === WsEntityType.DirectSaleDetail && (isDirectSaleDetailSnapshot(message) || isDirectSaleUserMetaSnapshot(message))) {
                    const { data: snapshotData } = message;

                    const cache = getCache<DirectSaleItemEntity>({ active: true });
                    const cacheData = cache?.state.data;

                    if (cacheData) {
                        cache.setData({ ...cacheData, ...snapshotData });
                    }

                    return true;
                }

                return false;
            };

            const getCache = <T>(options: { active?: boolean } = {}): Query<T> | undefined => {
                const caches = queryCache.findAll({
                    predicate,
                    ...(options.active && { type: 'active' }),
                });

                return caches[0] as Query<T> | undefined;
            };

            const isSetCacheEvent = (event: QueryCacheNotifyEvent): boolean =>
                event.type === 'updated' && event.action.type === 'success' && predicate(event.query);

            const isUnsetCacheEvent = (event: QueryCacheNotifyEvent): boolean =>
                event.type === 'observerRemoved' && event.query.observers.length === 0 && predicate(event.query);

            const updateObservedId = (data: unknown): void => {
                switch (entityType) {
                    case WsEntityType.AuctionsListing:
                        observedIds.value = (data as AuctionsListingResponseDto)?.auctions?.map(auction => auction.id);
                        break;
                    case WsEntityType.AuctionDetail:
                        observedIds.value = [(data as AuctionItemEntity).id];
                        break;
                    case WsEntityType.DirectSalesListing:
                        observedIds.value = (data as DirectSalesListingResponseDto)?.directSales?.map(directSale => directSale.id);
                        break;
                    case WsEntityType.DirectSaleDetail:
                        observedIds.value = [(data as DirectSaleItemEntity).id];
                        break;
                }
            };

            const handleCacheEvent = (event: QueryCacheNotifyEvent): void => {
                if (isSetCacheEvent(event)) {
                    updateObservedId(event.query.state.data);
                } else if (isUnsetCacheEvent(event)) {
                    observedIds.value = [];
                }
            };

            const initObserved = (): void => {
                const cache = getCache();
                const cacheData = cache?.state.data;

                if (cacheData) {
                    updateObservedId(cacheData);
                }
            };

            watch(observedIds, (value, oldValue) => {
                if (value.join() === oldValue.join() || status.value !== 'OPEN') return;
                sendObserveMessage();
            });

            queryCache.subscribe(handleCacheEvent);

            nuxtApp.hooks.hook('app:created', () => {
                initObserved();
            });

            return { sendObserveMessage, handleSnapshotMessage };
        };

        const handleAuthorizeSuccessMessage = makeBaseMessageHandler<AuthorizeSuccessWsEventResponseDto>(WsOutEvent.AuthorizeSuccess, () => {
            authHandshakeManager.resolve();
        });

        const handleUnauthorizeSuccessMessage = makeBaseMessageHandler<UnauthorizeSuccessWsEventResponseDto>(WsOutEvent.UnauthorizeSuccess, () => {
            authHandshakeManager.resolve();
        });

        const handleUserOutBidMessage = makeBaseMessageHandler<UserOutBidWsEventResponseDto>(WsOutEvent.UserOutBid, message => {
            let title: TranslationKey = 'notifications.userOutBid.title';
            let description: TranslationKey = 'notifications.userOutBid.description';

            if (message.data.reason === UserOutBidWsEventResponseDtoDataReason.OUTBID_BY_AUTOBID_BID_TIE) {
                title = 'notifications.userOutBid.titleAutoBidTie';
                description = 'notifications.userOutBid.descriptionAutoBidTie';
            } else if (message.data.reason === UserOutBidWsEventResponseDtoDataReason.OUTBID_BY_AUTOBID_BID) {
                title = 'notifications.userOutBid.titleAutoBid';
                description = 'notifications.userOutBid.descriptionAutoBid';
            }

            notifyWarning({
                title: tt(title),
                description: tt(description, {
                    subject: formatNotifySubject(message, 'notifications.userOutBid.inAuction', 'notifications.userOutBid.linkedAuction'),
                    prevBidAmount: tn(message.data.prevBidAmount, 'currency'),
                    currentBidAmount: tn(message.data.currentBidAmount, 'currency'),
                }),
                duration: NotificationDuration.NoTimeout,
                actions: formatAuctionLinkNotifyActions(message),
            });
        });

        const handleUserBailAuthorizedMessage = makeBaseMessageHandler<UserBailAuthorizedWsEventResponseDto>(
            WsOutEvent.UserBailAuthorized,
            message => {
                notifySuccess({
                    title: tt('notifications.userBailAuthorized.title'),
                    description: tt('notifications.userBailAuthorized.description', {
                        subject: formatNotifySubject(
                            message,
                            'notifications.userBailAuthorized.inAuction',
                            'notifications.userBailAuthorized.linkedAuction'
                        ),
                    }),
                    duration: NotificationDuration.NoTimeout,
                    actions: formatAuctionLinkNotifyActions(message),
                });
            }
        );

        const handleUserMaxBidderRestoredMessage = makeBaseMessageHandler<UserMaxBidderRestoredWsEventResponseDto>(
            WsOutEvent.UserMaxBidderRestored,
            message => {
                notifySuccess({
                    title: tt('notifications.userMaxBidderRestored.title'),
                    description: tt('notifications.userMaxBidderRestored.description', {
                        subject: formatNotifySubject(
                            message,
                            'notifications.userMaxBidderRestored.inAuction',
                            'notifications.userMaxBidderRestored.linkedAuction'
                        ),
                        prevBidAmount: tn(message.data.prevBidAmount, 'currency'),
                        currentBidAmount: tn(message.data.currentBidAmount, 'currency'),
                    }),
                    duration: NotificationDuration.NoTimeout,
                    actions: formatAuctionLinkNotifyActions(message),
                });
            }
        );

        const handleUserOffersUpdate = makeBaseMessageHandler<UserOffersUpdateWsEventResponseDto>(WsOutEvent.UserOffersUpdate, () => {
            if (routeBaseName(route) === 'account-bids') {
                queryClient.invalidateQueries({ queryKey: getApiAccountAuctionsBuyerQueryKey() });
            }
        });

        const handleAuctionWonMessage = makeBaseMessageHandler<AuctionWonWsEventResponseDto>(WsOutEvent.AuctionWon, message => {
            notifySuccess({
                title: tt(message.data.reservePriceReached ? 'notifications.auctionWon.title' : 'notifications.auctionWon.titleReservePriceNotMet'),
                description: tt(
                    message.data.reservePriceReached
                        ? 'notifications.auctionWon.description'
                        : 'notifications.auctionWon.descriptionReservePriceNotMet',
                    {
                        subject: formatNotifySubject(message, 'notifications.auctionWon.inAuction', 'notifications.auctionWon.linkedAuction'),
                        currentPrice: tn(message.data.currentPrice, 'currency'),
                    }
                ),
                duration: NotificationDuration.NoTimeout,
                actions: formatAuctionLinkNotifyActions(message),
            });

            updateTrackingCompanyMetrics();
        });

        const handleUserWinningBidMessage = makeBaseMessageHandler<UserWinningBidWsEventResponseDto>(WsOutEvent.UserWinningBid, message => {
            notifySuccess({
                title: tt('notifications.userWinningBid.title'),
                description: tt('notifications.userWinningBid.description', {
                    subject: formatNotifySubject(message, 'notifications.userWinningBid.inAuction', 'notifications.userWinningBid.linkedAuction'),
                    amount: tn(message.data.currentBidAmount, 'currency'),
                }),
                duration: NotificationDuration.NoTimeout,
                actions: formatAuctionLinkNotifyActions(message),
            });
        });

        const handleUserAutoBidMaxThresholdReachedMessage = makeBaseMessageHandler<UserAutoBidMaxThresholdReachedWsEventResponseDto>(
            WsOutEvent.UserAutoBidMaxThresholdReached,
            message => {
                notifyInfo({
                    title: tt('notifications.userAutoBidMaxThresholdReached.title'),
                    description: tt('notifications.userAutoBidMaxThresholdReached.description', {
                        subject: formatNotifySubject(
                            message,
                            'notifications.userAutoBidMaxThresholdReached.inAuction',
                            'notifications.userAutoBidMaxThresholdReached.linkedAuction'
                        ),
                        amount: tn(message.data.maximumAmount, 'currency'),
                    }),
                    duration: NotificationDuration.NoTimeout,
                    actions: formatAuctionLinkNotifyActions(message),
                });
            }
        );

        const handleNegotiationStartedMessage = makeBaseMessageHandler<NegotiationStartedWsEventResponseDto>(
            WsOutEvent.NegotiationStarted,
            message => {
                notifyInfo({
                    title: tt('notifications.negotiationStarted.title'),
                    description: tt('notifications.negotiationStarted.description', {
                        subject: formatNotifySubject(
                            message,
                            'notifications.negotiationStarted.inAuction',
                            'notifications.negotiationStarted.linkedAuction'
                        ),
                    }),
                    duration: NotificationDuration.NoTimeout,
                    actions: formatAuctionLinkNotifyActions(message, 'negotiation'),
                });
            }
        );

        const handleNegotiationExpiredMessage = makeBaseMessageHandler<NegotiationExpiredWsEventResponseDto>(
            WsOutEvent.NegotiationExpired,
            message => {
                notifyInfo({
                    title: tt('notifications.negotiationExpired.title'),
                    description: tt('notifications.negotiationExpired.description', {
                        subject: formatNotifySubject(
                            message,
                            'notifications.negotiationExpired.inAuction',
                            'notifications.negotiationExpired.linkedAuction'
                        ),
                    }),
                    duration: NotificationDuration.NoTimeout,
                    actions: formatAuctionLinkNotifyActions(message, 'negotiation'),
                });
            }
        );

        const handleNegotiationProposalByBuyerMessage = makeBaseMessageHandler<NegotiationProposalByBuyerWsEventResponseDto>(
            WsOutEvent.NegotiationProposalByBuyer,
            message => {
                notifyInfo({
                    title: tt('notifications.negotiationProposalByBuyer.title'),
                    description: tt('notifications.negotiationProposalByBuyer.description', {
                        subject: formatNotifySubject(
                            message,
                            'notifications.negotiationProposalByBuyer.inAuction',
                            'notifications.negotiationProposalByBuyer.linkedAuction'
                        ),
                        proposalAmount: tn(message.data.proposalAmount, 'currency'),
                    }),
                    duration: NotificationDuration.NoTimeout,
                    actions: formatAuctionLinkNotifyActions(message, 'negotiation'),
                });
            }
        );

        const handleNegotiationProposalBySellerMessage = makeBaseMessageHandler<NegotiationProposalBySellerWsEventResponseDto>(
            WsOutEvent.NegotiationProposalBySeller,
            message => {
                notifyInfo({
                    title: tt('notifications.negotiationProposalBySeller.title'),
                    description: tt('notifications.negotiationProposalBySeller.description', {
                        subject: formatNotifySubject(
                            message,
                            'notifications.negotiationProposalBySeller.inAuction',
                            'notifications.negotiationProposalBySeller.linkedAuction'
                        ),
                        proposalAmount: tn(message.data.proposalAmount, 'currency'),
                    }),
                    duration: NotificationDuration.NoTimeout,
                    actions: formatAuctionLinkNotifyActions(message, 'negotiation'),
                });
            }
        );

        const handleNegotiationAcceptedByBackofficeMessage = makeBaseMessageHandler<NegotiationAcceptedByBackofficeWsEventResponseDto>(
            WsOutEvent.NegotiationAcceptedByBackoffice,
            message => {
                notifySuccess({
                    title: tt('notifications.negotiationAcceptedByBackOffice.title'),
                    description: tt('notifications.negotiationAcceptedByBackOffice.description', {
                        subject: formatNotifySubject(
                            message,
                            'notifications.negotiationAcceptedByBackOffice.inAuction',
                            'notifications.negotiationAcceptedByBackOffice.linkedAuction'
                        ),
                        acceptedAmount: tn(message.data.acceptedAmount, 'currency'),
                    }),
                    duration: NotificationDuration.NoTimeout,
                    actions: formatAuctionLinkNotifyActions(message, 'negotiation'),
                });
            }
        );

        const handleNegotiationAcceptedByBuyerMessage = makeBaseMessageHandler<NegotiationAcceptedByBuyerWsEventResponseDto>(
            WsOutEvent.NegotiationAcceptedByBuyer,
            message => {
                notifySuccess({
                    title: tt('notifications.negotiationAcceptedByBuyer.title'),
                    description: tt('notifications.negotiationAcceptedByBuyer.description', {
                        subject: formatNotifySubject(
                            message,
                            'notifications.negotiationAcceptedByBuyer.inAuction',
                            'notifications.negotiationAcceptedByBuyer.linkedAuction'
                        ),
                        acceptedAmount: tn(message.data.acceptedAmount, 'currency'),
                    }),
                    duration: NotificationDuration.NoTimeout,
                    actions: formatAuctionLinkNotifyActions(message, 'negotiation'),
                });
            }
        );

        const handleNegotiationAcceptedBySellerMessage = makeBaseMessageHandler<NegotiationAcceptedBySellerWsEventResponseDto>(
            WsOutEvent.NegotiationAcceptedBySeller,
            message => {
                notifySuccess({
                    title: tt('notifications.negotiationAcceptedBySeller.title'),
                    description: tt('notifications.negotiationAcceptedBySeller.description', {
                        subject: formatNotifySubject(
                            message,
                            'notifications.negotiationAcceptedBySeller.inAuction',
                            'notifications.negotiationAcceptedBySeller.linkedAuction'
                        ),
                        acceptedAmount: tn(message.data.acceptedAmount, 'currency'),
                    }),
                    duration: NotificationDuration.NoTimeout,
                    actions: formatAuctionLinkNotifyActions(message, 'negotiation'),
                });
            }
        );

        const handleNegotiationDeclinedBySellerMessage = makeBaseMessageHandler<NegotiationDeclinedBySellerWsEventResponseDto>(
            WsOutEvent.NegotiationDeclinedBySeller,
            message => {
                notifyWarning({
                    title: tt('notifications.negotiationsDeclinedBySeller.title'),
                    description: tt('notifications.negotiationsDeclinedBySeller.description', {
                        subject: formatNotifySubject(
                            message,
                            'notifications.negotiationsDeclinedBySeller.inAuction',
                            'notifications.negotiationsDeclinedBySeller.linkedAuction'
                        ),
                        declinedAmount: tn(message.data.declinedAmount, 'currency'),
                    }),
                    duration: NotificationDuration.NoTimeout,
                    actions: formatAuctionLinkNotifyActions(message, 'negotiation'),
                });
            }
        );

        const handleNegotiationStoppedProposalsByBuyerMessage = makeBaseMessageHandler<NegotiationStoppedProposalByBuyerWsEventResponseDto>(
            WsOutEvent.NegotiationStoppedProposalsByBuyer,
            message => {
                notifyWarning({
                    title: tt('notifications.negotiationStoppedProposalsByBuyer.title'),
                    description: tt('notifications.negotiationStoppedProposalsByBuyer.description', {
                        subject: formatNotifySubject(
                            message,
                            'notifications.negotiationStoppedProposalsByBuyer.inAuction',
                            'notifications.negotiationStoppedProposalsByBuyer.linkedAuction'
                        ),
                        maxBuyerProposal: tn(message.data.maxBuyerProposal, 'currency'),
                    }),
                    duration: NotificationDuration.NoTimeout,
                    actions: formatAuctionLinkNotifyActions(message, 'negotiation'),
                });
            }
        );

        const handleDirectSalePurchasedMessage = makeBaseMessageHandler<DirectSalePurchasedWsEventResponseDto>(
            WsOutEvent.DirectSalePurchased,
            message => {
                if (isSalePage(message)) navigateTo(formatPurchaseFlowRoute({ orderId: message.data.orderId }));

                updateTrackingCompanyMetrics();
            }
        );

        const handleDirectSaleNegotiationExpiredMessage = makeBaseMessageHandler<DirectSaleNegotiationExpiredWsEventResponseDto>(
            WsOutEvent.DirectSaleNegotiationExpired,
            message => {
                notifyInfo({
                    title: tt('directSaleNegotiation.notifications.negotiationExpired.title'),
                    description: tt('directSaleNegotiation.notifications.negotiationExpired.description', {
                        subject: formatNotifySubject(
                            message,
                            'directSaleNegotiation.notifications.negotiationExpired.inDirectSale',
                            'directSaleNegotiation.notifications.negotiationExpired.linkedDirectSale'
                        ),
                    }),
                    duration: NotificationDuration.NoTimeout,
                    actions: formatDirectSaleLinkNotifyActions(message, 'negotiation'),
                });
            }
        );

        const handleDirectSaleNegotiationProposalByBuyerMessage = makeBaseMessageHandler<DirectSaleNegotiationProposalByBuyerWsEventResponseDto>(
            WsOutEvent.DirectSaleNegotiationProposalByBuyer,
            message => {
                notifyInfo({
                    title: tt('directSaleNegotiation.notifications.negotiationProposalByBuyer.title'),
                    description: tt('directSaleNegotiation.notifications.negotiationProposalByBuyer.description', {
                        subject: formatNotifySubject(
                            message,
                            'directSaleNegotiation.notifications.negotiationProposalByBuyer.inDirectSale',
                            'directSaleNegotiation.notifications.negotiationProposalByBuyer.linkedDirectSale'
                        ),
                        proposalAmount: tn(message.data.proposalAmount, 'currency'),
                    }),
                    duration: NotificationDuration.NoTimeout,
                    actions: formatDirectSaleLinkNotifyActions(message, 'negotiation'),
                });
            }
        );

        const handleDirectSaleNegotiationProposalBySellerMessage = makeBaseMessageHandler<DirectSaleNegotiationProposalBySellerWsEventResponseDto>(
            WsOutEvent.DirectSaleNegotiationProposalBySeller,
            message => {
                notifyInfo({
                    title: tt('directSaleNegotiation.notifications.negotiationProposalBySeller.title'),
                    description: tt('directSaleNegotiation.notifications.negotiationProposalBySeller.description', {
                        subject: formatNotifySubject(
                            message,
                            'directSaleNegotiation.notifications.negotiationProposalBySeller.inDirectSale',
                            'directSaleNegotiation.notifications.negotiationProposalBySeller.linkedDirectSale'
                        ),
                        proposalAmount: tn(message.data.proposalAmount, 'currency'),
                    }),
                    duration: NotificationDuration.NoTimeout,
                    actions: formatDirectSaleLinkNotifyActions(message, 'negotiation'),
                });
            }
        );

        const handleDirectSaleNegotiationAcceptedByBuyerMessage = makeBaseMessageHandler<DirectSaleNegotiationAcceptedByBuyerWsEventResponseDto>(
            WsOutEvent.DirectSaleNegotiationAcceptedByBuyer,
            message => {
                notifyInfo({
                    title: tt('directSaleNegotiation.notifications.negotiationAcceptedByBuyer.title'),
                    description: tt('directSaleNegotiation.notifications.negotiationAcceptedByBuyer.description', {
                        subject: formatNotifySubject(
                            message,
                            'directSaleNegotiation.notifications.negotiationAcceptedByBuyer.inDirectSale',
                            'directSaleNegotiation.notifications.negotiationAcceptedByBuyer.linkedDirectSale'
                        ),
                        acceptedAmount: tn(message.data.acceptedAmount, 'currency'),
                    }),
                    duration: NotificationDuration.NoTimeout,
                    actions: formatDirectSaleLinkNotifyActions(message, 'negotiation'),
                });
            }
        );

        const handleDirectSaleNegotiationAcceptedByBackofficeMessage =
            makeBaseMessageHandler<DirectSaleNegotiationAcceptedByBackofficeWsEventResponseDto>(
                WsOutEvent.DirectSaleNegotiationAcceptedByBackoffice,
                message => {
                    notifySuccess({
                        title: tt('directSaleNegotiation.notifications.negotiationAcceptedByBackOffice.title'),
                        description: tt('directSaleNegotiation.notifications.negotiationAcceptedByBackOffice.description', {
                            subject: formatNotifySubject(
                                message,
                                'directSaleNegotiation.notifications.negotiationAcceptedByBackOffice.inDirectSale',
                                'directSaleNegotiation.notifications.negotiationAcceptedByBackOffice.linkedDirectSale'
                            ),
                            acceptedAmount: tn(message.data.acceptedAmount, 'currency'),
                        }),
                        duration: NotificationDuration.NoTimeout,
                        actions: formatDirectSaleLinkNotifyActions(message, 'negotiation'),
                    });
                }
            );

        const handleDirectSaleNegotiationAcceptedBySellerMessage = makeBaseMessageHandler<DirectSaleNegotiationAcceptedBySellerWsEventResponseDto>(
            WsOutEvent.DirectSaleNegotiationAcceptedBySeller,
            message => {
                notifyInfo({
                    title: tt('directSaleNegotiation.notifications.negotiationAcceptedBySeller.title'),
                    description: tt('directSaleNegotiation.notifications.negotiationAcceptedBySeller.description', {
                        subject: formatNotifySubject(
                            message,
                            'directSaleNegotiation.notifications.negotiationAcceptedBySeller.inDirectSale',
                            'directSaleNegotiation.notifications.negotiationAcceptedBySeller.linkedDirectSale'
                        ),
                        acceptedAmount: tn(message.data.acceptedAmount, 'currency'),
                    }),
                    duration: NotificationDuration.NoTimeout,
                    actions: formatDirectSaleLinkNotifyActions(message, 'negotiation'),
                });
            }
        );

        const handleDirectSaleNegotiationAbortedDueToAnotherAcceptanceMessage =
            makeBaseMessageHandler<DirectSaleNegotiationAbortedDueToAnotherAcceptanceWsEventResponseDto>(
                WsOutEvent.DirectSaleNegotiationAbortedDueToAnotherAcceptance,
                message => {
                    notifyInfo({
                        title: tt('directSaleNegotiation.notifications.negotiationAbortedAnotherAcceptance.title'),
                        description: tt('directSaleNegotiation.notifications.negotiationAbortedAnotherAcceptance.description', {
                            subject: formatNotifySubject(
                                message,
                                'directSaleNegotiation.notifications.negotiationAbortedAnotherAcceptance.inDirectSale',
                                'directSaleNegotiation.notifications.negotiationAbortedAnotherAcceptance.linkedDirectSale'
                            ),
                        }),
                        duration: NotificationDuration.NoTimeout,
                        actions: formatDirectSaleLinkNotifyActions(message, 'negotiation'),
                    });
                }
            );

        const favoriteAssetsInitFallbackManager = ((): FavoriteAssetsInitFallbackManager => {
            const INIT_FALLBACK_TIMEOUT = 5000;
            let timeoutId: ReturnType<typeof setTimeout> | null = null;

            return {
                start(): void {
                    timeoutId = setTimeout(() => {
                        if (isLoggedIn.value && !isFavoriteAssetsInitialized.value) {
                            setFavoriteAssets([]);
                            logWarn(
                                `FavoriteAssets init fallback applied! Empty list due to missing message within timeout (${INIT_FALLBACK_TIMEOUT} ms) `,
                                {
                                    user: { id: authData.value?.user?.id, email: authData.value?.user?.email },
                                }
                            );
                        }
                    }, INIT_FALLBACK_TIMEOUT);
                },
                abort(): void {
                    timeoutId && clearTimeout(timeoutId);
                    timeoutId = null;
                },
            };
        })();

        const handleUserFavoriteAssetsSnapshotMessage = makeBaseMessageHandler<UserFavoriteAssetsSnapshotWsEventResponseDto>(
            WsOutEvent.UserFavoriteAssetsSnapshot,
            message => {
                favoriteAssetsInitFallbackManager.abort();
                const assetIds = message.data.map(item => item.assetId);
                setFavoriteAssets(assetIds);
            }
        );

        const handleUserFavoriteAssetsAddedMessage = makeBaseMessageHandler<UserFavoriteAssetsAddedWsEventResponseDto>(
            WsOutEvent.UserFavoriteAssetsAdded,
            message => {
                const assetIds = message.data.map(item => item.assetId);
                assetIds.forEach(addFavoriteAsset);
            }
        );

        const handleUserFavoriteAssetsRemovedMessage = makeBaseMessageHandler<UserFavoriteAssetsRemovedWsEventResponseDto>(
            WsOutEvent.UserFavoriteAssetsRemoved,
            message => {
                const assetIds = message.data.map(item => item.assetId);
                assetIds.forEach(deleteFavoriteAsset);
            }
        );

        const auctionListingManager = makeWsEntityManager(WsEntityType.AuctionsListing);
        const auctionDetailManager = makeWsEntityManager(WsEntityType.AuctionDetail);
        const directSaleListingManager = makeWsEntityManager(WsEntityType.DirectSalesListing);
        const directSaleDetailManager = makeWsEntityManager(WsEntityType.DirectSaleDetail);

        const { status, send } = useWebSocket(wsEndpoint, {
            autoReconnect: {
                ...webSocketConfig.autoReconnect,
                onFailed() {
                    logError('Error connecting WS:', { endpoint: wsEndpoint });
                },
            },

            heartbeat: {
                ...webSocketConfig.heartbeat,
                message: messageFormatter.ping(),
            },

            onConnected(ws) {
                if (isLoggedIn.value && rawToken.value) {
                    authHandshakeManager.init();
                    favoriteAssetsInitFallbackManager.start();
                    ws.send(messageFormatter.authorize(rawToken.value));
                }

                auctionListingManager.sendObserveMessage({ subscribeOnly: true });
                auctionDetailManager.sendObserveMessage({ subscribeOnly: true });
                directSaleListingManager.sendObserveMessage({ subscribeOnly: true });
                directSaleDetailManager.sendObserveMessage({ subscribeOnly: true });
            },

            onMessage(_, event) {
                const eventData = safeJsonParse<WsMessage>(event.data, 'WS /ws message');
                if (!eventData) return;

                for (const handler of [
                    auctionListingManager.handleSnapshotMessage,
                    auctionDetailManager.handleSnapshotMessage,
                    directSaleListingManager.handleSnapshotMessage,
                    directSaleDetailManager.handleSnapshotMessage,
                    handleAuthorizeSuccessMessage,
                    handleUnauthorizeSuccessMessage,
                    handleUserOutBidMessage,
                    handleUserBailAuthorizedMessage,
                    handleUserMaxBidderRestoredMessage,
                    handleUserOffersUpdate,
                    handleAuctionWonMessage,
                    handleUserWinningBidMessage,
                    handleUserAutoBidMaxThresholdReachedMessage,
                    handleNegotiationStartedMessage,
                    handleNegotiationExpiredMessage,
                    handleNegotiationProposalByBuyerMessage,
                    handleNegotiationProposalBySellerMessage,
                    handleNegotiationAcceptedByBuyerMessage,
                    handleNegotiationAcceptedBySellerMessage,
                    handleNegotiationDeclinedBySellerMessage,
                    handleNegotiationStoppedProposalsByBuyerMessage,
                    handleDirectSalePurchasedMessage,
                    handleDirectSaleNegotiationExpiredMessage,
                    handleDirectSaleNegotiationProposalByBuyerMessage,
                    handleDirectSaleNegotiationProposalBySellerMessage,
                    handleDirectSaleNegotiationAcceptedByBuyerMessage,
                    handleDirectSaleNegotiationAcceptedBySellerMessage,
                    handleDirectSaleNegotiationAbortedDueToAnotherAcceptanceMessage,
                    handleDirectSaleNegotiationAcceptedByBackofficeMessage,
                    handleNegotiationAcceptedByBackofficeMessage,
                    handleUserFavoriteAssetsSnapshotMessage,
                    handleUserFavoriteAssetsAddedMessage,
                    handleUserFavoriteAssetsRemovedMessage,
                ]) {
                    if (handler(eventData)) return;
                }
            },
        });

        watch(isLoggedIn, (value, oldValue) => {
            if (value === oldValue || status.value !== 'OPEN') return;
            authHandshakeManager.init();
            favoriteAssetsInitFallbackManager[value ? 'start' : 'abort']();
            send(value && rawToken.value ? messageFormatter.authorize(rawToken.value) : messageFormatter.unauthorize());
        });
    },
});
